import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { ChevronDown, Menu, X, Sparkles, Zap, Shield, Building2, Leaf, Truck, GraduationCap, Users, Landmark, Target, Heart, Home, Store, Handshake, ArrowRight } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';
import { trackEvent } from '../hooks/useTrackEvent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping nom (DB) → composant lucide. Étendre ALLOWED_ICONS côté backend en parallèle.
const ICON_MAP = { Sparkles, Zap, Shield, Building2, Leaf, Truck, GraduationCap, Users, Landmark, Target, Heart, Home, Store, Handshake };

// Classes COMPLÈTES (littérales) pour que Tailwind JIT ne les purge pas.
const ICON_BG = {
  blue: 'bg-blue-500/20', purple: 'bg-purple-500/20', green: 'bg-green-500/20',
  emerald: 'bg-emerald-500/20', teal: 'bg-teal-500/20', cyan: 'bg-cyan-500/20',
  amber: 'bg-amber-500/20', orange: 'bg-orange-500/20', red: 'bg-red-500/20',
  pink: 'bg-pink-500/20', slate: 'bg-slate-500/20',
};
const ICON_FG = {
  blue: 'text-blue-400', purple: 'text-purple-400', green: 'text-green-400',
  emerald: 'text-emerald-400', teal: 'text-teal-400', cyan: 'text-cyan-400',
  amber: 'text-amber-400', orange: 'text-orange-400', red: 'text-red-400',
  pink: 'text-pink-400', slate: 'text-slate-400',
};
const HIGHLIGHT_TEXT = {
  blue: 'font-semibold text-blue-400 hover:text-blue-300 hover:bg-blue-500/10',
  purple: 'font-semibold text-purple-400 hover:text-purple-300 hover:bg-purple-500/10',
  green: 'font-semibold text-green-400 hover:text-green-300 hover:bg-green-500/10',
  emerald: 'font-semibold text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10',
  teal: 'font-semibold text-teal-400 hover:text-teal-300 hover:bg-teal-500/10',
  cyan: 'font-semibold text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10',
  amber: 'font-semibold text-amber-400 hover:text-amber-300 hover:bg-amber-500/10',
  orange: 'font-semibold text-orange-400 hover:text-orange-300 hover:bg-orange-500/10',
  red: 'font-semibold text-red-400 hover:text-red-300 hover:bg-red-500/10',
  pink: 'font-semibold text-pink-400 hover:text-pink-300 hover:bg-pink-500/10',
  slate: 'font-semibold text-slate-300 hover:text-white hover:bg-slate-800/50',
};
const HIGHLIGHT_TEXT_MOBILE = {
  blue: 'font-semibold text-blue-400', purple: 'font-semibold text-purple-400',
  green: 'font-semibold text-green-400', emerald: 'font-semibold text-emerald-400',
  teal: 'font-semibold text-teal-400', cyan: 'font-semibold text-cyan-400',
  amber: 'font-semibold text-amber-400', orange: 'font-semibold text-orange-400',
  red: 'font-semibold text-red-400', pink: 'font-semibold text-pink-400',
  slate: 'font-semibold text-slate-300',
};

// Largeur du dropdown selon le menu (faithful) — fallback w-72.
const MENU_WIDTH = {
  accueil: 'w-64', coppam: 'w-72', activites: 'w-80', fogedom: 'w-80', ftpe: 'w-72',
};

// Structure de secours (identique à la navbar historique) si l'API est KO.
const DEFAULT_MENUS = [
  { key: 'accueil', label: 'Accueil', icon: 'Sparkles', items: [
    { id: 'a1', label: 'Qui sommes-nous ?', to: '/qui-sommes-nous', icon: 'Users', color: 'blue' },
    { id: 'a2', label: 'Nos services', to: '/services', icon: 'Zap', color: 'purple' },
    { id: 'a3', label: 'Nos établissements', to: '/etablissements', icon: 'Building2', color: 'green' },
    { id: 'a4', label: 'Actualités', to: '/actualites', icon: 'Sparkles', color: 'cyan' },
    { id: 'a4b', label: 'Partenaires & Sponsors', to: '/partenaires-sponsors', icon: 'Heart', color: 'emerald' },
    { id: 'a5', label: 'Adhérer à O\u2019SCOP', to: '/adhesion', icon: 'Sparkles', color: 'amber', highlight: true, divider_before: true },
  ] },
  { key: 'activites', label: 'Dispositifs', icon: 'Zap', items: [
    { id: 'ac0', label: 'Communityplace', to: 'https://centrale.objectifscopoutremer.com/', icon: 'Users', color: 'cyan', badge_new: true, children: [
      { id: 'ac0a', label: 'Professionnels', to: 'https://centrale.objectifscopoutremer.com/', icon: 'Sparkles', color: 'blue' },
      { id: 'ac0b', label: 'Particuliers', to: 'https://centrale.objectifscopoutremer.com/particuliers', icon: 'Sparkles', color: 'blue' },
      { id: 'ac0c', label: "D\u00e9taillants-POP\'S", to: 'https://centrale.objectifscopoutremer.com/detaillant', icon: 'Store', color: 'pink', badge_new: true },
    ] },
    { id: 'ac1', label: "O\u2019SCOP GE - Groupement d\u2019Employeurs", to: '/oscop-ge', icon: 'Users', color: 'blue' },
    { id: 'ac2', label: "FORMA\u2019SCOP - Formation", to: '/formascop', icon: 'GraduationCap', color: 'purple' },
    { id: 'ac3', label: 'Logistique & Mobilit\u00e9', to: '/logistique-mobilite', icon: 'Truck', color: 'orange' },
    { id: 'ac4', label: 'AGRO & V\u00e9g\u00e9tal', to: '/agro-vegetal', icon: 'Leaf', color: 'green' },
    { id: 'ac5', label: 'B\u00e2timent & Expertise Civile', to: '/batiment-expertise', icon: 'Building2', color: 'amber', children: [
      { id: 'ac5a', label: 'Construction bois', to: 'https://batiscop.oscop.fr', icon: 'Leaf', color: 'green', badge_new: true },
    ] },
    { id: 'ac6', label: 'Garanties solidaires', to: '/garanties-solidaires-btp', icon: 'Shield', color: 'green' },
    { id: 'ac7', label: 'Innovation sociale', to: '/innovation-sociale', icon: 'Sparkles', color: 'purple' },
    { id: 'ac8', label: 'RCDS BATICO', to: '/rcds-batico', icon: 'Shield', color: 'blue' },
  ] },
];

const IconBadge = ({ icon, color }) => {
  const Ico = ICON_MAP[icon] || Sparkles;
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ICON_BG[color] || ICON_BG.slate}`}>
      <Ico className={`w-4 h-4 ${ICON_FG[color] || ICON_FG.slate}`} />
    </div>
  );
};

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to);

// Rend un lien interne (react-router), externe (nouvel onglet) ou un simple
// en-tête non cliquable (quand `to` est absent, ex: parent de sous-entrées).
const NavLink = ({ to, className, onClick, children, testId }) => {
  if (isExternal(to)) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} data-testid={testId}>
        {children}
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} className={className} onClick={onClick} data-testid={testId}>
        {children}
      </Link>
    );
  }
  return (
    <div className={className} data-testid={testId}>
      {children}
    </div>
  );
};

const Navigation = ({ mobileInFlow = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileKey, setOpenMobileKey] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menus, setMenus] = useState(DEFAULT_MENUS);
  const [bannerOpen, setBannerOpen] = useState(true);
  const [forcedOpenKey, setForcedOpenKey] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ouverture programmatique d'un menu du header (ex: bouton "Découvrir nos dispositifs")
  useEffect(() => {
    const handleOpenNav = (e) => {
      const wanted = String(e?.detail || '').toLowerCase();
      const match = menus.find((m) =>
        (m.key || '').toLowerCase() === wanted ||
        /dispositif|activit/i.test(m.label || '')
      );
      if (match) {
        setForcedOpenKey(match.id || match.key);
        setTimeout(() => setForcedOpenKey(null), 4000);
      }
    };
    window.addEventListener('oscop:open-nav', handleOpenNav);
    return () => window.removeEventListener('oscop:open-nav', handleOpenNav);
  }, [menus]);

  // Charge la config depuis la base ; fallback silencieux sur DEFAULT_MENUS.
  // Envoie le token si présent → le backend filtre par audience (connecté/rôle).
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('admin_token');
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    axios.get(`${API}/public/frontoffice-nav`, cfg)
      .then((res) => {
        const fetched = res?.data?.menus;
        if (!cancelled && Array.isArray(fetched) && fetched.length > 0) {
          setMenus(fetched);
        }
      })
      .catch(() => { /* garde DEFAULT_MENUS */ });
    return () => { cancelled = true; };
  }, []);

  const menuActive = (menu) => (menu.items || []).some((it) => location.pathname === it.to);

  return (
    <nav
      data-testid="public-navbar"
      className={`${mobileInFlow ? 'sticky lg:fixed' : 'fixed'} top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/50 shadow-2xl shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      {/* Bannière d'annonce Communityplace (home uniquement) */}
      {bannerOpen && location.pathname === '/' && (
        <div data-testid="communityplace-banner" className="relative bg-gradient-to-r from-pink-600 via-fuchsia-600 to-orange-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 pr-10 flex items-center justify-center gap-2 sm:gap-3 flex-wrap text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ring-white/30">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
              </span>
              Nouveau
            </span>
            <Store className="h-4 w-4 shrink-0" />
            <span className="text-xs sm:text-sm font-semibold">
              Découvrez <strong>Communityplace</strong>, la nouvelle place de marché solidaire O'SCOP.
            </span>
            <a
              href="https://centrale.objectifscopoutremer.com/#pros"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="communityplace-banner-cta"
              className="group inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-bold text-fuchsia-700 shadow-sm transition-all hover:scale-105"
            >
              J'y vais
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
          <button
            onClick={() => setBannerOpen(false)}
            data-testid="communityplace-banner-close"
            aria-label="Fermer l'annonce"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white z-10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <img
                src="/oscop-logo-new.png"
                alt="OBJECTIF SCOP OUTREMER"
        className="h-8 sm:h-12 md:h-14 w-auto max-w-[250px] sm:max-w-none relative z-10 group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>

          {/* Desktop Navigation (data-driven) */}
          <div className="hidden lg:flex items-center space-x-1" data-testid="navbar-desktop-menus">
            {menus.map((menu) => {
              const MenuIco = ICON_MAP[menu.icon] || Sparkles;
              const width = MENU_WIDTH[menu.key] || 'w-72';
              // Menu lien direct externe (ex. « Associé Coopérateur ») : pas de dropdown
              if (menu.direct_to) {
                return (
                  <a
                    key={menu.id || menu.key}
                    href={menu.direct_to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-menu-${menu.key}`}
                    className="px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800/50"
                  >
                    <MenuIco className="w-4 h-4" />
                    {menu.label}
                    {menu.badge_new && (
                      <span data-testid="nav-menu-badge-nouveau" className="ml-1 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm ring-1 ring-inset ring-white/20 animate-pulse">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                        </span>
                        Nouveau
                      </span>
                    )}
                  </a>
                );
              }
              return (
                <div key={menu.id || menu.key} className="relative group" data-testid={`nav-menu-${menu.key}`} onMouseLeave={() => setForcedOpenKey(null)}>
                  <button
                    className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 text-sm font-medium ${
                      menuActive(menu)
                        ? 'bg-slate-800/60 text-white border border-slate-700/60'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    <MenuIco className="w-4 h-4" />
                    {menu.label}
                    <ChevronDown className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
                  </button>
                  <div className={`absolute left-0 mt-2 ${width} bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 ${forcedOpenKey === (menu.id || menu.key) ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'} group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 transform overflow-hidden max-h-[80vh] overflow-y-auto`}>
                    <div className="p-2">
                      {(menu.items || []).map((it) => (
                        <React.Fragment key={it.id}>
                          {it.divider_before && <div className="border-t border-slate-700/50 my-2" />}
                          <NavLink
                            to={it.to}
                            testId={`nav-item-${it.id}`}
                            className={`flex items-center gap-3 px-4 py-3 text-sm rounded-xl transition-all ${
                              !it.to
                                ? 'text-white font-semibold cursor-default bg-gradient-to-r from-pink-500/15 to-orange-500/10 border border-pink-500/20 mb-1'
                                : it.highlight
                                ? (HIGHLIGHT_TEXT[it.color] || HIGHLIGHT_TEXT.slate)
                                : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                            }`}
                          >
                            <IconBadge icon={it.icon} color={it.color} />
                            <span className="flex-1">{it.emoji ? `${it.emoji} ${it.label}` : it.label}</span>
                            {(it.badge_new || (it.label || '').toLowerCase().includes('communityplace')) && (
                              <span data-testid="nav-badge-nouveau" className="ml-auto inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm ring-1 ring-inset ring-white/20 animate-pulse">
                                <span className="relative flex h-1.5 w-1.5">
                                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                                </span>
                                Nouveau
                              </span>
                            )}
                          </NavLink>
                          {Array.isArray(it.children) && it.children.length > 0 && (
                            <div className="ml-6 pl-3 border-l border-slate-700/40 space-y-1 mb-1">
                              {it.children.map((sub) => (
                                <NavLink
                                  key={sub.id}
                                  to={sub.to}
                                  testId={`nav-subitem-${sub.id}`}
                                  className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all"
                                >
                                  <IconBadge icon={sub.icon} color={sub.color} />
                                  <span className="flex-1">{sub.emoji ? `${sub.emoji} ${sub.label}` : sub.label}</span>
                                  {sub.badge_new && (
                                    <span data-testid="nav-badge-nouveau" className="ml-auto inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm ring-1 ring-inset ring-white/20 animate-pulse">
                                      <span className="relative flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                                      </span>
                                      Nouveau
                                    </span>
                                  )}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Langue + Boutons (Desktop) */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <ThemeToggle />
            <Link to="/login">
              <Button
                variant="outline"
                className="border border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800/50 hover:border-slate-500 font-medium rounded-xl px-5 transition-all duration-300"
              >
                {t('nav.login')}
              </Button>
            </Link>
            <Link to="/register">
              <Button
                onClick={() => trackEvent('cta.become_member', { from: 'navigation_desktop' })}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 rounded-xl px-5 transition-all duration-300 hover:scale-105"
              >
                {t('nav.become_member')}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800/50 transition-all"
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation (data-driven) */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6 space-y-2 animate-in slide-in-from-top duration-300 bg-slate-900/95 backdrop-blur-xl rounded-2xl mt-2 border border-slate-700/50 p-4" data-testid="navbar-mobile-menus">
            {menus.map((menu) => {
              const MenuIco = ICON_MAP[menu.icon] || Sparkles;
              const isOpen = openMobileKey === (menu.id || menu.key);
              // Menu lien direct externe : navigation directe, pas d'accordéon
              if (menu.direct_to) {
                return (
                  <a
                    key={menu.id || menu.key}
                    href={menu.direct_to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-menu-${menu.key}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-xl transition-all"
                  >
                    <span className="font-medium flex items-center gap-2"><MenuIco className="w-4 h-4" /> {menu.label}</span>
                    {menu.badge_new && (
                      <span data-testid="nav-menu-badge-nouveau-mobile" className="ml-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white animate-pulse">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                        </span>
                        Nouveau
                      </span>
                    )}
                  </a>
                );
              }
              return (
                <div key={menu.id || menu.key}>
                  <button
                    onClick={() => setOpenMobileKey(isOpen ? null : (menu.id || menu.key))}
                    className="w-full flex items-center justify-between px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-xl transition-all"
                  >
                    <span className="font-medium flex items-center gap-2"><MenuIco className="w-4 h-4" /> {menu.label}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-2 space-y-1 bg-slate-800/30 rounded-xl p-2">
                      {(menu.items || []).map((it) => (
                        <React.Fragment key={it.id}>
                          <NavLink
                            to={it.to}
                            testId={`nav-mobile-item-${it.id}`}
                            className={`block px-4 py-2.5 text-sm rounded-lg transition-all ${
                              !it.to
                                ? 'text-white font-semibold'
                                : it.highlight ? (HIGHLIGHT_TEXT_MOBILE[it.color] || HIGHLIGHT_TEXT_MOBILE.slate) : 'text-slate-400 hover:text-white'
                            }`}
                            onClick={it.to ? () => setMobileMenuOpen(false) : undefined}
                          >
                            {it.emoji ? `${it.emoji} ${it.label}` : it.label}
                          </NavLink>
                          {Array.isArray(it.children) && it.children.length > 0 && (
                            <div className="ml-4 pl-3 border-l border-slate-700/40 space-y-1">
                              {it.children.map((sub) => (
                                <NavLink
                                  key={sub.id}
                                  to={sub.to}
                                  testId={`nav-mobile-subitem-${sub.id}`}
                                  className="flex items-center gap-2 px-4 py-2 text-xs rounded-lg text-slate-400 hover:text-white transition-all"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <span className="flex-1">{sub.emoji ? `${sub.emoji} ${sub.label}` : sub.label}</span>
                                  {sub.badge_new && (
                                    <span data-testid="nav-badge-nouveau-mobile" className="ml-auto inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white animate-pulse">
                                      Nouveau
                                    </span>
                                  )}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Langue (Mobile) */}
            <div className="px-4 pt-4 mt-4 border-t border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Langue & Thème</p>
                <ThemeToggle />
              </div>
              <LanguageSelector />
            </div>

            {/* Boutons (Mobile) */}
            <div className="flex flex-col gap-3 px-4 pt-4 mt-4 border-t border-slate-700/50">
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-xl">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register" onClick={() => { trackEvent('cta.become_member', { from: 'navigation_mobile' }); setMobileMenuOpen(false); }}>
                <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 rounded-xl">
                  {t('nav.become_member')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
